import { HttpClient, ListParams } from './http-client';
import Animal from '../interfaces/animal';
import Paginate from '../interfaces/paginate';
import AnimalGroup from '../interfaces/animal-group';
import AnimalNote from '../interfaces/animal-note';
import { Observable } from 'rxjs';

interface Option {
  label: string;
  value: string;
}

export class AnimalService extends HttpClient {
  index(
    farmId: string,
    params?: ListParams,
    getAll?: boolean
  ): Observable<Paginate<Animal>> {
    return this.get<Paginate<Animal>>(
      `/api/farms/${farmId}/livestock/animals`,
      { ...params, getAll }
    );
  }

  indexPost(
    farmId: string,
    params?: ListParams,
    getAll?: boolean
  ): Observable<Paginate<Animal>> {
    return this.post<Paginate<Animal>>(
      `/api/farms/${farmId}/livestock/animals/search`,
      { ...params, getAll }
    );
  }

  show(farmId: string, id: string): Observable<Animal> {
    return this.get<Animal>(`/api/farms/${farmId}/livestock/animals/${id}`);
  }

  update(farmId: string, id: string, data: any): Observable<Animal> {
    return this.put<Animal>(
      `/api/farms/${farmId}/livestock/animals/${id}`,
      data
    );
  }

  syncShow(farmId: string, id: string): Observable<Animal> {
    return this.post<Animal>(
      `/api/farms/${farmId}/livestock/animals/sync/${id}`
    );
  }

  usedAnimalGroups(farmId: string) {
    return this.get<AnimalGroup[]>(
      `/api/farms/${farmId}/livestock/used-animal-groups`
    );
  }

  notes(farmId: string, animalId: string) {
    return this.get<AnimalNote[]>(
      `/api/farms/${farmId}/livestock/animals/${animalId}/notes`
    );
  }

  storeNote(farmId: string, animalId: string, note: AnimalNote) {
    return this.post<AnimalNote>(
      `/api/farms/${farmId}/livestock/animals/${animalId}/note`,
      note
    );
  }

  updateNote(
    farmId: string,
    animalId: string,
    noteId: string,
    note: AnimalNote
  ) {
    return this.put<AnimalNote>(
      `/api/farms/${farmId}/livestock/animals/${animalId}/note/${noteId}`,
      note
    );
  }

  deleteNote(farmId: string, animalId: string, noteId: string) {
    return this.delete<any>(
      `/api/farms/${farmId}/livestock/animals/${animalId}/note/${noteId}`
    );
  }

  species(farmId: string, params?: any) {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/species-filter-values`,
      params
    );
  }

  animalGroups(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/categories-filter-values`,
      params
    );
  }

  genders(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/gender-filter-values`,
      params
    );
  }

  breeds(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/breed-filter-values`,
      params
    );
  }

  barns(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/barn-filter-values`,
      params
    );
  }

  animalLdcNumbers(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/animal-ldc-nr-filter-values`,
      params
    );
  }

  animalEids(farmId: string, params?: any): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/livestock/animal-eid-filter-values`,
      params
    );
  }

  exportXls(farmId: string, params?: ListParams): Observable<object> {
    return this.export(`/api/farms/${farmId}/livestock/animals`, params);
  }

  syncAnimals(farmId: string, params?: ListParams): Observable<void> {
    return this.post<null>(
      `/api/farms/${farmId}/livestock/animals/sync`,
      params
    );
  }

  syncAnimalWeighingData(
    farmId: string,
    animalId,
    params?: ListParams
  ): Observable<any> {
    return this.get<any>(
      `/api/farms/${farmId}/livestock/animal-group/animal-weighing-data/${animalId}`,
      params
    );
  }
}
